import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  JSX,
} from 'solid-js';
import { PointCloudStylingUOM } from '@trimble/types';
import {
  convertFeetInchesToMeters,
  convertMetersToFeetInches,
  INCHES_PER_FOOT,
} from '@trimble/utilities';
import { PointCloudUOMInputControlProps } from './point-cloud-uom-input.model';
import { CustomInput } from '@tcweb/connect-ui';

// Constants for magic numbers
const DEFAULT_LENGTH_DECIMALS = 2;
const DEFAULT_LENGTH_FRACTIONS = 16;

export const PointCloudUOMInputControl: Component<
  PointCloudUOMInputControlProps
> = (props: PointCloudUOMInputControlProps) => {
  // Internal value in meters
  let internalValue = 0;

  // Track if component is being destroyed to prevent destruction blur commits
  let isBeingDestroyed = false;

  // UOM configuration memos
  const uom = createMemo(() => props.uom);
  const valueInMeters = createMemo(() => props.valueInMeters);
  const showFeetInches = createMemo(() => uom().feetInches ?? false);

  const lengthDecimals = createMemo(() => {
    const decimals = uom().lengthDecimals ?? DEFAULT_LENGTH_DECIMALS;
    return decimals < 0 ? 0 : decimals;
  });

  const lengthFractions = createMemo(
    () => uom().lengthFractions ?? DEFAULT_LENGTH_FRACTIONS
  );

  // Initial display values
  const initialDecimal = createMemo(() => {
    const currentUom = uom();
    return (props.valueInMeters * currentUom.meterToUnitFactor).toFixed(
      lengthDecimals()
    );
  });

  const initialFeetInches = createMemo(() => {
    if (showFeetInches()) {
      return convertMetersToFeetInches(props.valueInMeters, lengthFractions());
    }
    return { feet: 0, inches: 0, fraction: 0 };
  });

  // Display value signals
  const [decimal, setDecimal] = createSignal<string>('');
  const [feet, setFeet] = createSignal<number>(0);
  const [inches, setInches] = createSignal<number>(0);
  const [fraction, setFraction] = createSignal<number>(0);

  createEffect(
    on(uom, (currentUom) => {
      setDisplaySignals(internalValue, currentUom);
    })
  );

  // External value overrides internal value
  createEffect(
    on(valueInMeters, (value) => {
      internalValue = value;
      setDisplaySignals(value, uom());
    })
  );

  // Initialize display values
  createEffect(() => {
    setDecimal(initialDecimal());
    const feetInches = initialFeetInches();
    setFeet(feetInches.feet);
    setInches(feetInches.inches);
    setFraction(feetInches.fraction);
  });

  onCleanup(() => {
    isBeingDestroyed = true;
  });

  const setDisplaySignals = (
    value: number,
    uom: PointCloudStylingUOM
  ): void => {
    if (showFeetInches()) {
      const { feet, inches, fraction } = convertMetersToFeetInches(
        value,
        lengthFractions()
      );
      setFeet(feet);
      setInches(inches);
      setFraction(fraction);
    } else {
      const displayValue = value * uom.meterToUnitFactor;
      setDecimal(displayValue.toFixed(lengthDecimals()));
    }
  };

  const onBlur = (): void => {
    if (!isBeingDestroyed && props.onCommit) {
      props.onCommit(internalValue);
    }
  };

  const onFocus = (): void => {
    props.onFocus?.();
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' && props.onCommit) {
      props.onCommit(internalValue);
    }
  };

  const onDecimalChange = (e: Event): void => {
    const target = e.target as HTMLInputElement | undefined;
    if (!target) return;
    const newInputValue = target.value;

    if (newInputValue === '') {
      props.onError?.('Value is required');
      return;
    }

    const numericValue = parseFloat(newInputValue);
    if (!isNaN(numericValue) && isFinite(numericValue)) {
      const value = numericValue / uom().meterToUnitFactor;
      internalValue = value;
      props.onChange?.(value);
    }
  };

  const createFractionHandler =
    (setter: (val: number) => void, maxValue?: () => number) =>
    // eslint-disable-next-line solid/reactivity
    (e: Event): void => {
      const target = e.target as HTMLInputElement | undefined;
      if (!target) return;
      const value = target.value;

      if (value === '') {
        props.onError?.('Invalid value');
        return;
      }

      const numericValue = parseFloat(value);
      if (!isNaN(numericValue) && isFinite(numericValue)) {
        if (maxValue && numericValue >= maxValue()) {
          props.onError?.('Invalid value');
          return;
        }

        setter(numericValue);
        const fractions = lengthFractions();
        const value = convertFeetInchesToMeters(
          feet(),
          inches(),
          fraction(),
          fractions
        );
        internalValue = value;
        props.onChange?.(value);
      }
    };

  const onFeetChange = createFractionHandler(setFeet);

  const onInchesChange = createFractionHandler(
    setInches,
    () => INCHES_PER_FOOT
  );

  const onFractionChange = createFractionHandler(setFraction, () =>
    // eslint-disable-next-line solid/reactivity
    lengthFractions()
  );

  const renderDecimalInput = (): JSX.Element => (
    <div class="w-full flex items-center gap-2">
      <CustomInput
        name="point-cloud-uom-decimal-input"
        label=""
        class="flex-1"
        value={decimal()}
        placeholder={`Enter value in ${uom().unitLabel}`}
        onInput={onDecimalChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
      />
      {(props.showUomLabel ?? true) && (
        <span class="text-sm text-gray-600 min-w-4">{uom().unitLabel}</span>
      )}
    </div>
  );

  const renderFeetInchesInput = (): JSX.Element => (
    <div class="w-full flex items-center gap-1">
      <CustomInput
        name="point-cloud-uom-feet-input"
        label=""
        placeholder="ft"
        class="min-w-16 flex-1"
        value={feet().toString()}
        onInput={onFeetChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
      />
      {(props.showUomLabel ?? true) && <span class="text-sm">ft</span>}

      <CustomInput
        name="point-cloud-uom-inches-input"
        label=""
        placeholder="in"
        class="w-11"
        value={inches().toString()}
        onInput={onInchesChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
      />

      <CustomInput
        name="point-cloud-uom-fraction-input"
        label=""
        class="w-11"
        value={fraction().toString()}
        onInput={onFractionChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
      />
      <span class="text-sm whitespace-nowrap">
        /&nbsp;{lengthFractions()} in
      </span>
    </div>
  );

  return (
    <div class="w-full">
      {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
      {showFeetInches() ? renderFeetInchesInput() : renderDecimalInput()}
    </div>
  );
};
